import { getAuthInstance } from '@/lib/firebase';
import { setApiClientIdToken } from '@/lib/apiClient';
import {
  onAuthStateChanged,
  signInWithCustomToken,
  signOut,
  updateProfile,
  type User
} from 'firebase/auth';
import { create } from 'zustand';

export type AuthState = 'loading' | 'signed-in' | 'signed-out';

export interface AuthStoreState {
  authState: AuthState;
  user: User | null;
  init: () => void;
  signInWithToken: (args: {
    token: string;
    displayName?: string;
    profileImageUrl?: string;
  }) => Promise<User>;
  signOut: () => Promise<void>;
}

let unsubscribeAuth: (() => void) | null = null;

export const useAuthStore = create<AuthStoreState>((set, get) => ({
  authState: 'loading',
  user: null,

  init: () => {
    if (unsubscribeAuth) return;

    const auth = getAuthInstance();
    if (!auth) {
      set({ authState: 'signed-out', user: null });
      return;
    }

    unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const idToken = await user.getIdToken();
        setApiClientIdToken(idToken);
      } else {
        setApiClientIdToken(null);
      }
      set({ authState: user ? 'signed-in' : 'signed-out', user: user ?? null });
    });
  },

  signInWithToken: async ({ token, displayName, profileImageUrl }) => {
    const auth = getAuthInstance();
    if (!auth) throw new Error('Firebase is not configured.');

    const credential = await signInWithCustomToken(auth, token);
    if (displayName || profileImageUrl) {
      await updateProfile(credential.user, {
        displayName: displayName ?? undefined,
        photoURL: profileImageUrl ?? undefined
      });
    }

    const idToken = await credential.user.getIdToken();
    setApiClientIdToken(idToken);

    get().init();
    set({ authState: 'signed-in', user: credential.user });
    return credential.user;
  },

  signOut: async () => {
    const auth = getAuthInstance();
    if (!auth) return;
    await signOut(auth);
    setApiClientIdToken(null);
    set({ authState: 'signed-out', user: null });
  }
}));
